/**
 * Slice 11 — Cashier user identity + offline-password API client.
 *
 * The endpoints live under `/v1/users` on the merchant API. The shared
 * `@dhuttu/contracts` TS client has not been regenerated against the
 * Slice-11 OpenAPI bump yet, so the types are hand-written here; once the
 * client publishes the new operations they can be swapped out without
 * touching the call sites.
 */
import { env } from '../env';

export type UserRole = 'CASHIER' | 'MANAGER';

export interface UserView {
  userId: number;
  email: string;
  displayName: string;
  role: UserRole;
  storeId: string | null;
  active: boolean;
  offlinePasswordSet: boolean;
  offlinePasswordUpdatedAt: string | null;
  createdAt: string;
}

export interface UserListResponse {
  items: UserView[];
}

export interface CreateUserRequest {
  email: string;
  displayName: string;
  role: UserRole;
  storeId: string;
}

export interface PatchUserRequest {
  displayName?: string;
  role?: UserRole;
  storeId?: string;
  active?: boolean;
}

export interface ApiErrorBody {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  code?: string;
}

/** Error thrown by the user API helpers. Carries the parsed problem body. */
export class UsersApiError extends Error {
  status: number;
  code: string | undefined;
  detail: string | undefined;
  constructor(status: number, body: ApiErrorBody) {
    super(body.title || body.detail || `HTTP ${status}`);
    this.name = 'UsersApiError';
    this.status = status;
    this.code = body.code;
    this.detail = body.detail;
  }
}

async function request<T>(
  getToken: () => Promise<string>,
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const token = await getToken();
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (method !== 'GET') {
    headers['Idempotency-Key'] = crypto.randomUUID();
  }
  const res = await fetch(`${env.apiBaseUrl}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    let parsed: ApiErrorBody = {};
    try { parsed = (await res.json()) as ApiErrorBody; } catch { /* not JSON */ }
    throw new UsersApiError(res.status, parsed);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export function makeUsersApi(getToken: () => Promise<string>) {
  return {
    listUsers: (): Promise<UserListResponse> =>
      request<UserListResponse>(getToken, 'GET', '/users'),

    createUser: (req: CreateUserRequest): Promise<UserView> =>
      request<UserView>(getToken, 'POST', '/users', req),

    patchUser: (id: number, partial: PatchUserRequest): Promise<UserView> =>
      request<UserView>(getToken, 'PATCH', `/users/${id}`, partial),

    setOfflinePassword: (id: number, password: string): Promise<void> =>
      request<void>(getToken, 'PUT', `/users/${id}/offline-password`, { password }),

    clearOfflinePassword: (id: number): Promise<void> =>
      request<void>(getToken, 'DELETE', `/users/${id}/offline-password`),
  };
}

export type UsersApiClient = ReturnType<typeof makeUsersApi>;
